import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export default function FAQs() {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const faqs = [
    {
      question: "What is HorseRacingOracle?",
      answer: "HorseRacingOracle is an automated betting bot that uses advanced algorithms to identify and place bets on qualifying horse races. It analyses multiple factors including odds, ratings, and form to make informed betting decisions, aiming to generate consistent profits while managing risk."
    },
    {
      question: "Once I purchase, how will I receive access to HorseRacingOracle?",
      answer: "After your purchase, you'll be redirected to a thank you page where you'll receive a unique verification code. You'll use this code to create your account, after which you can connect your Betfair account and start using HorseRacingOracle."
    },
    {
      question: "What is the bot's strategy?",
      answer: "The bot automatically places bets on qualifying horse races using your Betfair account. You can choose between two staking strategies to suit your risk and reward preferences. The Daily Profit Target strategy aims to achieve a set daily profit percentage (Maximum of 2% of your balance) while the Percentage Staking strategy bets a fixed percentage of your live balance (e.g., 2% or 5%)."
    },
    {
      question: "What is a daily profit target?",
      answer: "A daily profit target is a limit you set on how much profit the bot will aim to make in a single day. This helps manage your bankroll and ensures sustainable, long-term trading. Once the daily target is reached, the bot will stop placing bets for that day."
    },
    {
      question: "How does the bot select races to bet on?",
      answer: "The bot analyses multiple factors including number of runners, odds, official ratings, and recent form to identify qualifying races with strong favorites."
    },
    {
      question: "What balance amount do I need to start?",
      answer: " In order for the bot to operate properly, we recommend a minimum starting balance of £250 for the Daily Profit Target strategy and a minimum stake per bet of £1 for the Percentage Staking strategy (as Betfair has a minimum stake of £1 per bet)."
    },
    {
      question: "What is the maximum daily profit target?",
      answer: "To help protect your balance and manage your funds, our system allows a maximum daily profit target of 2% of your account balance."
    },
    {
      question: "How do I connect my Betfair account?",
      answer: "Go to the 'Connect Betfair Account' page and follow the authentication process. You'll need to authorise our HorseRacingOracle application to place bets on your behalf."
    },
    {
      question: "What happens if I lose internet connection?",
      answer: "The bot will continue to run even if your internet cuts off or your computer is switched off. In the unlikely scenario where we have issues connecting to Betfair's servers then the bot will stop operating until connection is restored."
    },
    {
      question: "Can I run the bot 24/7?",
      answer: "Yes, the bot can run continuously, but it will only place bets during active racing hours and when qualifying races are found."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-block mb-8">
            <img
              src="/HRO-Logo-transparent-white-logo.png"
              alt="Horse Racing Oracle"
              className="h-12"
            />
          </Link>
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Frequently Asked Questions
          </h1>
        </div>

        <div className="mb-8 max-w-2xl mx-auto text-left text-sm text-gray-600 space-y-3">
          <p>
            For a fuller overview of automation on UK Betfair markets, strict filters, and what happens after you
            connect your account, read our{' '}
            <Link to="/how-it-works" className="text-indigo-600 hover:text-indigo-500 font-medium">
              How it works
            </Link>{' '}
            guide.
          </p>
          <p>
            Staking modes, daily limits, and bankroll considerations are explained in{' '}
            <Link to="/staking-and-risk-controls" className="text-indigo-600 hover:text-indigo-500 font-medium">
              Staking &amp; risk controls
            </Link>
            . Gambling involves risk; only stake what you can afford to lose.
          </p>
        </div>

        {/* FAQ Section */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                  <button
                    className="w-full text-left"
                    onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  >
                    <div className="flex justify-between items-center">
                      <h3 className="text-base font-medium text-gray-900">{faq.question}</h3>
                      <svg
                        className={`h-5 w-5 text-gray-500 transform transition-transform ${
                          expandedFAQ === index ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  {expandedFAQ === index && (
                    <p className="mt-2 text-sm text-gray-500">{faq.answer}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="text-center mt-8">
          <p className="text-base text-gray-600">
            If you have any further questions, please{' '}
            <Link to="/contact" className="text-indigo-600 hover:text-indigo-500 font-medium">
              Contact Us
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
} 